import { useRef, useState, PointerEvent } from "react";
import { useScale } from "common/utils";
import { IconArrowLeft, IconArrowRight } from "common/assets";
import { LevelButton } from "./LevelButton";
import { MapDecorations } from "./MapDecorations";
import { SOKOBAN_LEVELS, SOKOBAN_MAP_WIDTH, SOKOBAN_MAP_HEIGHT } from "./levels";

const VIEW_WIDTH = 1118;
const SCROLL_STEP = 560;

const calcInitialOffset = (levelId: number) => {
  const target = SOKOBAN_LEVELS.find((l) => l.id === levelId);
  if (!target) return 0;
  const desired = target.x - VIEW_WIDTH / 2;
  const maxOffset = Math.max(0, SOKOBAN_MAP_WIDTH - VIEW_WIDTH);
  return Math.max(0, Math.min(maxOffset, desired));
};

export const LevelMap = ({
  onLevelPress,
  initialLevelId,
}: {
  onLevelPress: (levelId: number) => void;
  initialLevelId: number;
}) => {
  const { scale } = useScale();
  const [offsetX, setOffsetX] = useState(() => calcInitialOffset(initialLevelId));
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startOffset: 0, moved: false });

  const maxOffset = Math.max(0, SOKOBAN_MAP_WIDTH - VIEW_WIDTH);
  const clamp = (x: number) => Math.max(0, Math.min(maxOffset, x));

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    setDragging(true);
    dragRef.current = { startX: e.clientX, startOffset: offsetX, moved: false };
    // setPointerCapture をここで呼ぶと PC で click が親 div に固定されて子の LevelButton.onClick が
    // 発火しなくなる。実際にドラッグが始まった（moved=true）タイミングで capture を起動する。
  };
  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const rawDx = e.clientX - dragRef.current.startX;
    if (Math.abs(rawDx) > 4 && !dragRef.current.moved) {
      dragRef.current.moved = true;
      // ドラッグが確定した時点で capture（指/カーソルが要素外に出てもイベントを拾う）
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (!dragRef.current.moved) return;
    const dx = rawDx / Math.max(scale, 0.0001);
    setOffsetX(clamp(dragRef.current.startOffset - dx));
  };
  const handlePointerUp = () => setDragging(false);
  const handleArrow = (dir: -1 | 1) => () => setOffsetX((prev) => clamp(prev + dir * SCROLL_STEP));

  const handleLevelPress = (levelId: number) => () => {
    if (dragRef.current.moved) return;
    onLevelPress(levelId);
  };

  return (
    <div className="flex-col-el w-full h-full relative overflow-hidden bg-gradient-to-b from-[#bfe4f5] to-[#7fc4dc]">
      <div
        className="absolute top-0 left-0 will-change-transform"
        style={{
          width: SOKOBAN_MAP_WIDTH,
          height: SOKOBAN_MAP_HEIGHT,
          transform: `translateX(${-offsetX}px)`,
          transition: dragging ? "none" : "transform 300ms ease-out",
          touchAction: "pan-y",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <MapDecorations />

        {SOKOBAN_LEVELS.map((level) => (
          <LevelButton
            key={level.id}
            level={level}
            isFocused={level.id === initialLevelId}
            onPress={handleLevelPress(level.id)}
          />
        ))}
      </div>

      <div className="flex-row-el flex-center upsilon absolute bottom-4 left-3 pointer-events-auto cursor-pointer z-50">
        <button className="btn btn-primary w-[40px] h-[72px]" onClick={handleArrow(-1)}>
          <div className="flex-col-center mb-1">
            <IconArrowLeft />
          </div>
        </button>
      </div>

      <div className="flex-row-el flex-center upsilon absolute bottom-4 right-3 pointer-events-auto cursor-pointer z-50">
        <button className="btn btn-primary w-[40px] h-[72px]" onClick={handleArrow(1)}>
          <div className="flex-col-center mb-1">
            <IconArrowRight />
          </div>
        </button>
      </div>
    </div>
  );
};
